
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Order, Driver, SystemConfig, DeliveryProvider, OperatingContext, SmartSplitSuggestion } from '../types';
import { calculateRoutingRecommendationAsync } from '../services/logistics/strategies/recommendationEngine';
import { reorganizeQueueSmartly } from '../services/logistics/strategies/queue';
import { isScheduledRoutingEligible } from '../services/logistics/core/scheduled';
import { DEFAULT_DECISION_POLICY_PROFILE, getDecisionPolicyWeights } from '../constants';
import { TIMING, KITCHEN, LIMITS, SCORING, TIME_LIMITS } from '../constants';

const DEFAULT_SUGGESTION: SmartSplitSuggestion = {
    ownFleet: [], thirdParty: [], heldOrders: [], routes: [], thirdPartyTripsCount: 0,
    metrics: { breakdown: { ownCash: 0, thirdPartyCash: 0, ownDecision: 0, thirdPartyDecision: 0 }, cashDelta: { chosen: 0, alternative: 0, signedDelta: 0, label: 'ECONOMIA_CAIXA', reason: 'UNKNOWN' } },
    isSplitRecommended: false, optimizationConstrainedBySLA: false, estimatedTime: 0, minSla: 999, lowestSlack: 999,
    decisionTrace: []
};

const RECALC_INTERVAL_MS = TIMING.RECALC_INTERVAL_MS;

export const useSalIntelligence = (
    orders: Order[],
    drivers: Driver[],
    providers: DeliveryProvider[],
    config: SystemConfig,
    context: OperatingContext,
    storeId?: string
) => {

    const [rawSmartSplit, setRawSmartSplit] = useState<SmartSplitSuggestion>(DEFAULT_SUGGESTION);
    const [pendingOrders, setPendingOrders] = useState<Order[]>([]);
    const [nextUpdate, setNextUpdate] = useState<number>(Date.now() + TIMING.NEXT_UPDATE_BUFFER_MS);
    const [secondsToNextUpdate, setSecondsToNextUpdate] = useState(0);
    const lastPoolHash = useRef<string>('');

    const todayOrders = useMemo(() => {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startTs = startOfToday.getTime();
        return orders.filter(o => o.createdAt >= startTs);
    }, [orders]);

    // 2. COLUNA DE TRIAGEM
    // v41.15: 'assigned' removido da triagem pois já está no Monitor Ativo (Aguardando Motoboy)
    // v57.10: Garantir explícitamente que 'ready' está na triagem
    // v44.7: Pedidos com Ghost Dispatch ativo PERMANECEM na triagem mesmo com status 'dispatched'
    useEffect(() => {
        const triageStatuses = ['pending', 'payment_pending', 'confirmed', 'scheduled_confirmed', 'ready', 'waiting_confirmation'];
        const triagePool = todayOrders.filter(o => 
            triageStatuses.includes(o.status) || 
            (o.ghostDispatch && o.status === 'dispatched')  // Ghost Dispatch: mantém na triagem mesmo quando dispatched
        );
        const organized = reorganizeQueueSmartly(triagePool, config.businessLat, config.businessLng, config.fleetConfig.clusteringMaxRadiusKm, config.fleetConfig.clusteringMaxAngleDegrees, config.fleetConfig.clusteringMinDistanceForAngleCheck, config.fleetConfig.maxOrdersPerRoute, config.fleetConfig.slaToleranceMinutes, config.averageKitchenTime, config.fleetConfig.zigzagIgnoreTimeMinutes, config, drivers, providers, context, rawSmartSplit);
        setPendingOrders(organized);
    }, [todayOrders, config, drivers, providers, context, rawSmartSplit]);

    // 2. MONITOR OPERACIONAL (FROTA PRÓPRIA)
    // v41.8: Correção de Ordens Entregues Persistentes
    // v57.4: FIX - Pedidos só devem aparecer no monitor após terem driverId (despacho efetivo)
    const assignedOrders = useMemo(() => todayOrders.filter(o =>
        ['assigned', 'dispatched', 'delivered'].includes(o.status) &&
        !!o.driverId // Apenas pedidos com motorista atribuído
    ).filter(o => {
        // Se o pedido ainda não foi entregue, deve aparecer.
        if (o.status !== 'delivered') return true;

        // Se foi entregue, verificamos se o motorista tem ALGUM pedido ativo ('assigned' ou 'dispatched').
        // Isso garante que o pedido entregue suma assim que a missão acabar.
        const hasActiveMission = todayOrders.some(other =>
            other.driverId === o.driverId &&
            (other.status === 'assigned' || other.status === 'dispatched')
        );

        return hasActiveMission;
    }), [todayOrders]);

    // 3. MONITOR OPERACIONAL (APPS EXTERNOS)
    // COLUNA "REDE EXTERNA" / ENVIOS EXTERNOS
    // Exibe APENAS pedidos sugeridos/pendentes para third-party (não despachados)
    // Após despacho, o pedido vai para Monitoramento e sai desta coluna
    const isExternalInTransit = useCallback((order: Order) =>
        order.status === 'dispatched_3rd_party' ||
        (order.status === 'dispatched' && order.manualAssignment === 'third')
    , []);

    const hasAnyExternalInTransit = useMemo(() => todayOrders.some(isExternalInTransit), [todayOrders, isExternalInTransit]);

    const thirdPartyOrders = useMemo(() => todayOrders.filter(order => {
        // BUG FIX: Não mostrar pedidos já despachados nesta coluna
        // Pedidos despachados (dispatched_3rd_party) vão para Monitoramento, não para esta coluna
        if (isExternalInTransit(order)) return false;

        // Mostrar apenas pedidos com atribuição manual third-party PENDENTE (não despachada ainda)
        // Estes são os pedidos sugeridos para envio externo que ainda não foram despachados
        if (order.manualAssignment === 'third' && order.status !== 'dispatched_3rd_party') {
            return true;
        }

        // Lógica para pedidos entregues: manter se cluster tem outros em trânsito
        if (order.status !== 'delivered') return false;

        if (order.smartClusterId) {
            return todayOrders.some(other =>
                other.id !== order.id &&
                isExternalInTransit(other) &&
                other.smartClusterId === order.smartClusterId
            );
        }

        return hasAnyExternalInTransit;
    }), [todayOrders, isExternalInTransit, hasAnyExternalInTransit]);

    // 3. MONITOR OPERACIONAL (OUTROS ENVIOS)
    // Pedidos que entraram em trânsito via API/PDV sem despacho explícito do SAL.
    // CRITICAL: Pedidos com Ghost Dispatch ativo NÃO devem aparecer aqui - permanecem na triagem
    const isOtherInTransit = useCallback((order: Order) =>
        order.status === 'dispatched' &&
        !order.driverId &&
        order.manualAssignment !== 'third' &&
        !order.dispatchRequestedAt &&
        !order.ghostDispatch  // v44.7: Excluir pedidos em Ghost Dispatch
    , []);

    const hasAnyOtherInTransit = useMemo(() => todayOrders.some(isOtherInTransit), [todayOrders, isOtherInTransit]);

    const otherOrders = useMemo(() => todayOrders.filter(order => {
        if (isOtherInTransit(order)) return true;
        if (order.status !== 'delivered') return false;

        if (order.smartClusterId) {
            return todayOrders.some(other =>
                other.id !== order.id &&
                isOtherInTransit(other) &&
                other.smartClusterId === order.smartClusterId
            );
        }

        return hasAnyOtherInTransit;
    }), [todayOrders, isOtherInTransit, hasAnyOtherInTransit]);

    useEffect(() => {
        let isMounted = true;
        const runEngine = async () => {
            const scheduledLeadMinutes = config.cardapioWeb?.scheduledRoutingLeadMinutes ?? TIMING.DEFAULT_SCHEDULED_LEAD_MINUTES;
            const routingPool = todayOrders.filter(o =>
                ['confirmed', 'scheduled_confirmed', 'ready'].includes(o.status) &&
                o.type === 'DELIVERY' &&
                (!o.manualAssignment || o.isManuallyAdjusted) &&
                isScheduledRoutingEligible(o, scheduledLeadMinutes)
            );
            if (routingPool.length === 0) { if (isMounted) setRawSmartSplit(DEFAULT_SUGGESTION); return; }
            const currentHash = routingPool.map(o => `${o.id}:${o.status}:${o.isReady}:${o.pickupConfirmed ? 1 : 0}:${o.manualAssignment || ''}:${o.isManuallyAdjusted}:${(o.ignoredStrategies || []).join(',')}:${(o.acceptedStrategies || []).join(',')}`).sort().join('|');

            // v46.6: Se o hash mudou, ignoramos o throttle para dar feedback instantâneo ao usuário
            const forceUpdate = currentHash !== lastPoolHash.current;

            if (forceUpdate || Date.now() >= nextUpdate) {
                try {
                    // v46.9: ORACLE & KITCHEN INTELLIGENCE (Local Implementation)

                    // 1. Kitchen Metrics (Moving Average of Prep Time)
                    // v46.11: ROLLING WINDOW (Last 12 Orders)
                    // Solves the "Pronta Entrega" skew issue. We only care about the MOST RECENT performance.
                    const recentPrepTimes = todayOrders
                        .filter(o => o.status === 'ready' && o.createdAt && o.statusUpdatedAt)
                        .sort((a, b) => (b.statusUpdatedAt || 0) - (a.statusUpdatedAt || 0)) // Newest first
                        .slice(0, config.kitchenRollingWindowSize || KITCHEN.DEFAULT_ROLLING_WINDOW_SIZE) // Rolling Window

                        .map(o => (o.statusUpdatedAt || Date.now()) - o.createdAt);

                    // Default to config average if not enough data
                    const avgPrepMs = recentPrepTimes.length >= (config.kitchenRollingWindowSize || KITCHEN.DEFAULT_ROLLING_WINDOW_SIZE) // Require configured samples to form an opinion
                        ? recentPrepTimes.reduce((a, b) => a + b, 0) / recentPrepTimes.length
                        : (config.averageKitchenTime || KITCHEN.DEFAULT_AVERAGE_PREP_TIME_MIN) * TIMING.MS_PER_MINUTE;

                    const kitchenMetrics: import('../types').KitchenMetrics = {
                        effectivePrepTime: Math.ceil(avgPrepMs / TIMING.MS_PER_MINUTE),
                        efficiencyRatio: (avgPrepMs / TIMING.MS_PER_MINUTE) / (config.averageKitchenTime || KITCHEN.DEFAULT_AVERAGE_PREP_TIME_MIN),
                        currentQueueSize: todayOrders.filter(o => o.status === 'confirmed').length
                    };

                    // 2. Oracle State (Predictive Peak Analysis)
                    const now = new Date();
                    const currentHour = now.getHours();
                    const hourlyShares = config.oracle?.hourlyShare || [];
                    const policyProfile = config.oracle?.decisionPolicyProfile ?? DEFAULT_DECISION_POLICY_PROFILE;
                    const decisionScoringWeights = getDecisionPolicyWeights(policyProfile);

                    const peakThresholdFactor = Math.min(
                        0.92,
                        Math.max(
                            0.68,
                            Number((0.88 - (decisionScoringWeights.slaPriority * 0.2) + (decisionScoringWeights.marginPriority * 0.0666667)).toFixed(2))
                        )
                    );
                    const lookaheadHours = Math.max(3, Math.min(6, Math.round(2 + (decisionScoringWeights.slaPriority * 4))));
                    const surgeMultiplier = Math.max(7, Math.min(14, Math.round(8 + (decisionScoringWeights.slaPriority * 4))));

                    // v46.10: Adaptive Peak Detection
                    // Instead of a fixed %, we calculate the "High Water Mark" of the day.
                    // A "Peak" is defined as any hour effectively close (e.g. 80%) to the daily maximum.
                    const maxDailyShare = Math.max(...hourlyShares, 0.04); // Floor at 4% to avoid noise in flat days
                    const peakThreshold = maxDailyShare * peakThresholdFactor;

                    let nextPeakHour = -1;
                    for (let h = 1; h <= lookaheadHours; h++) {
                        const targetHour = (currentHour + h) % 24;
                        if ((hourlyShares[targetHour] || 0) >= peakThreshold) {
                            nextPeakHour = targetHour;
                            break;
                        }
                    }

                    // If peak found, set time to start of that hour. If not, set to far future.
                    const peakTime = nextPeakHour !== -1
                        ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), nextPeakHour, 0, 0).getTime()
                        : now.getTime() + TIMING.MS_PER_DAY; // Tomorrow

                    // Handle Peak Crossing (e.g. peak is 00:00 tomorrow)
                    const finalPeakTime = peakTime < now.getTime() ? peakTime + TIMING.MS_PER_DAY : peakTime;

                    const oracleState: import('../types').OracleState = {
                        predictedPeakTime: finalPeakTime,
                        surgeProbability: config.oracle?.active ? Math.min(1, (hourlyShares[nextPeakHour] || 0) * surgeMultiplier) : 0,
                        currentLoadLevel: kitchenMetrics.currentQueueSize > LIMITS.HIGH_QUEUE_THRESHOLD ? 'high' : 'medium'
                    };

                    const runtimeConfig: SystemConfig = config.oracle
                        ? {
                            ...config,
                            oracle: {
                                ...config.oracle,
                                decisionPolicyProfile: policyProfile,
                                decisionScoringWeights
                            }
                        }
                        : config;

                    // Seletor via config.routingRecommendationMode:
                    // - advanced: comportamento atual completo
                    // - simplified: regras fixas e previsiveis, sem alterar o modal de configuracoes
                    const rawSuggestion = await calculateRoutingRecommendationAsync(
                        routingPool,
                        runtimeConfig,
                        drivers,
                        providers,
                        context,
                        storeId,
                        oracleState,
                        kitchenMetrics
                    );
                    if (isMounted) {
                        setRawSmartSplit(rawSuggestion);
                        lastPoolHash.current = currentHash;
                        setNextUpdate(Date.now() + RECALC_INTERVAL_MS);
                    }
                } catch (error) { }
            }
        };
        runEngine();
        return () => { isMounted = false; };
    }, [todayOrders, config, drivers, providers, context, nextUpdate, storeId]);

    const historicalOrders = useMemo(() => orders.filter(o => ['delivered', 'archived', 'returned', 'canceled'].includes(o.status)), [orders]);

    return { smartSplit: rawSmartSplit, pendingOrders, assignedOrders, thirdPartyOrders, otherOrders, historicalOrders, secondsToNextUpdate };
};
